import { EllipsisMenuItemStatus } from '@platform/ui-library';
import {
  FC,
  useCallback,
  useEffect,
  useState
} from 'react';
import {
  LanguageLabel,
  ToolbarDropdown,
  useLanguageParts
} from '@app/form_components';
import {
  useAsync,
  useDesign,
  useServices
} from '@app/hooks';
import { useGlobalSettings } from '@app/stores';
import {
  defaultLanguage,
  defaultTtsVoice,
  DesignLanguage,
  LanguageCode
} from '@app/types';
import { isLanguageMatch } from '@app/utilities';

type LanguageSettingSelectorProps = {
  /**
   * When false, labels omit language/country (prompt panel).
   * @default true
   */
  includeLanguage?: boolean;
  /**
   * When set, only variants of this language are listed (prompt panel).
   * Global toolbar/locale omit this and list every design language.
   */
  languageCode?: LanguageCode | null;
  onChange?: () => void;
};

type LanguageOption = {
  language: DesignLanguage;
  languageCode: LanguageCode;
};

const optionKey = (option: LanguageOption): string => (
  [
    option.languageCode,
    option.language.voice ?? '',
    option.language.ttsFallBackVoice ?? ''
  ].join('|')
);

/** Resolves EllipsisMenu string labels (ReactNode labels break item keys). */
const MenuLabelResolver: FC<{
  includeLanguage: boolean;
  onResolve: (key: string, label: string) => void;
  option: LanguageOption;
}> = ({
  includeLanguage,
  onResolve,
  option
}) => {
  const key = optionKey(option);
  const parts = useLanguageParts({
    includeLanguage,
    includeTts: true,
    includeVt: true,
    language: option.language
  });
  const label = parts?.join(' ') || key;

  useEffect(() => {
    onResolve(key, label);
  }, [key, label, onResolve]);

  return null;
};

export const LanguageSettingSelector: FC<LanguageSettingSelectorProps> = ({
  includeLanguage = true,
  languageCode: scopedLanguageCode,
  onChange
}) => {
  const serialized = useDesign();
  const { workflowService } = useServices();
  const {
    language,
    setSettings,
    ttsVoices,
    voice
  } = useGlobalSettings();
  const isScoped = scopedLanguageCode != null;
  const resolvedLanguageCode = scopedLanguageCode ?? language ?? defaultLanguage;
  const [menuLabels, setMenuLabels] = useState<Record<string, string>>({});
  const [catalogLanguages] = useAsync(() => {
    return workflowService.getLanguages();
  }, [workflowService]);

  const options: LanguageOption[] = (
    serialized.languages
    .filter(({ language: code }) => (
      (!isScoped) ? true : code === scopedLanguageCode
    ))
    .map((entry) => ({
      language: entry,
      languageCode: entry.language
    }))
  );

  const ttsVoice = ttsVoices[resolvedLanguageCode] ?? defaultTtsVoice;

  const handleChange = (option: LanguageOption) => {
    const { language: designLanguage, languageCode } = option;
    const nextVoice = designLanguage.voice || null;
    const nextTts = designLanguage.ttsFallBackVoice ?? defaultTtsVoice;
    if (isScoped) {
      setSettings({
        ttsVoices: {
          ...ttsVoices,
          [languageCode]: nextTts
        },
        voice: nextVoice
      });
    } else {
      setSettings({
        language: languageCode,
        ttsVoices: {
          ...ttsVoices,
          [languageCode]: nextTts
        },
        voice: nextVoice
      });
      window.apm.serialized.voice = nextVoice ?? undefined;
      window.apm.postEventToCurrentView({ target: 'request-refresh' });
    }
    onChange?.();
  };

  const isOptionSelected = (option: LanguageOption) => (
    isLanguageMatch(option.language, {
      languageCode: (isScoped) ? resolvedLanguageCode : (language ?? defaultLanguage),
      ttsVoice: ttsVoices[option.languageCode] ?? defaultTtsVoice,
      voice
    })
  );

  const currentOption = (
    options.find(isOptionSelected)
    ?? options.find((option) => (
      isLanguageMatch(option.language, {
        languageCode: resolvedLanguageCode,
        ttsVoice
      })
    ))
    ?? options.find(({ languageCode }) => languageCode === resolvedLanguageCode)
  );
  const currentOptionKey = (currentOption) ? optionKey(currentOption) : null;

  const onResolveLabel = useCallback((key: string, label: string) => {
    setMenuLabels((prev) => (
      (prev[key] === label) ? prev : { ...prev, [key]: label }
    ));
  }, []);

  useEffect(() => {
    if (options.length < 1) {
      return;
    }
    if (isScoped) {
      if (options.length >= 2 && (ttsVoice === defaultTtsVoice || !currentOptionKey)) {
        const fallback = options[0];
        const nextTts = fallback.language.ttsFallBackVoice ?? defaultTtsVoice;
        const nextVoice = fallback.language.voice || null;
        if (nextTts !== ttsVoice || nextVoice !== voice) {
          handleChange(fallback);
        }
      }
      return;
    }
    if (!language || !options.some(({ languageCode }) => languageCode === language)) {
      const fallback = (
        options.find(({ languageCode }) => languageCode === defaultLanguage)
        ?? options[0]
      );
      handleChange(fallback);
      return;
    }
    if (!options.some(isOptionSelected)) {
      const fallback = (
        options.find(({ languageCode }) => languageCode === language)
        ?? options[0]
      );
      handleChange(fallback);
    }
  }, [currentOptionKey, isScoped, language, resolvedLanguageCode, ttsVoice, voice, serialized.languages]);

  // Store always seeds `language`; LanguageLabel returns null until catalog loads —
  // wait for both so ToolbarDropdown never mounts as an empty chevron.
  const hasCatalogLabel = !!catalogLanguages?.some(({ value }) => value === resolvedLanguageCode);
  if ((isScoped && options.length < 2) || !currentOption || !hasCatalogLabel) {
    return null;
  }

  const items = options.map((option) => {
    const key = optionKey(option);
    return {
      action: () => handleChange(option),
      // EllipsisMenu keys rows by `label` — must be a unique string, not a ReactNode.
      label: menuLabels[key] ?? key,
      status: (option === currentOption) ? EllipsisMenuItemStatus.Selected : undefined
    };
  });

  return (
    <>
      {options.map((option) => (
        <MenuLabelResolver
          key={optionKey(option)}
          includeLanguage={includeLanguage}
          onResolve={onResolveLabel}
          option={option}
        />
      ))}
      <ToolbarDropdown className="selector" items={items}>
        <LanguageLabel
          includeLanguage={includeLanguage}
          includeTts
          includeVt
          language={currentOption.language}
        />
      </ToolbarDropdown>
    </>
  );
};
